import React, { useEffect, useImperativeHandle, useRef } from 'react';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ID = 'cf-turnstile-script';

export interface TurnstileHandle {
  reset: () => void;
}

interface TurnstileProps {
  siteKey: string;
  onSuccess: (token: string) => void;
  onExpire: () => void;
}

const Turnstile = React.forwardRef<TurnstileHandle, TurnstileProps>(
  ({ siteKey, onSuccess, onExpire }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | undefined>(undefined);

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current !== undefined && (window as any).turnstile) {
          (window as any).turnstile.reset(widgetIdRef.current);
        }
      },
    }));

    useEffect(() => {
      const render = () => {
        if (
          containerRef.current &&
          (window as any).turnstile &&
          widgetIdRef.current === undefined
        ) {
          widgetIdRef.current = (window as any).turnstile.render(
            containerRef.current,
            {
              sitekey: siteKey,
              callback: onSuccess,
              'expired-callback': onExpire,
            },
          );
        }
      };

      if ((window as any).turnstile) {
        render();
      } else {
        if (!document.getElementById(SCRIPT_ID)) {
          const script = document.createElement('script');
          script.id = SCRIPT_ID;
          script.src = SCRIPT_URL;
          script.async = true;
          script.defer = true;
          document.head.appendChild(script);
        }
        const script = document.getElementById(SCRIPT_ID) as HTMLScriptElement;
        script.addEventListener('load', render);
        return () => script.removeEventListener('load', render);
      }

      return () => {
        if (widgetIdRef.current !== undefined && (window as any).turnstile) {
          (window as any).turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = undefined;
        }
      };
    }, []);

    return <div ref={containerRef} />;
  },
);

Turnstile.displayName = 'Turnstile';

export default Turnstile;
